// =============================================================================
// pages/CustomerDashboard.jsx
//
// Customer-facing dashboard:
//   1. Optional PAN nudge (dismissible) — only useful for CA-managed flows
//   2. Sync bar — for self-syncing customers with active subscription
//   3. My Companies — Tally companies the user has access to (self-sync OR
//      CA-managed). Aggregated by company name so a single Tally company
//      shows once even across multiple book periods or owners.
//      Expand a company → list of all completed syncs across its sources
//      Click a sync → onOpenSync({ source_id, sync_id, visible_pages })
//        → App.jsx sets view='audit', navigates to the full
//          sidebar-driven audit view (same layout the CA sees)
// =============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { PageWrap, Skeleton, useIsMobile } from '../components/UI';
import AgentSyncBar from '../components/AgentSyncBar';
import { api } from '../api';
import '../styles/CustomerDashboard.css';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:3000';

function getAuthHeaders() {
  try {
    const saved = localStorage.getItem("auditpros_user");
    if (saved) {
      const parsed = JSON.parse(saved);
      const account = parsed?.account;
      const email = account?.idTokenClaims?.email
        || account?.idTokenClaims?.preferred_username
        || account?.idTokenClaims?.emails?.[0]
        || account?.username || account?.preferred_username || account?.email;
      if (email) return { "Content-Type": "application/json", "x-user-email": email };
    }
  } catch { /* ignore */ }
  return { "Content-Type": "application/json" };
}

function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
}

function fmtDateTime(d) {
  if (!d) return '—';
  try {
    return `${new Date(d).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false, timeZone: 'Asia/Kolkata',
    }).replace(',', '')} IST`;
  } catch { return d; }
}

// ── Audit status badge ────────────────────────────────────────────────────────
const STATUS_CFG = {
  in_progress:      { label: 'In Progress',  color: 'var(--accent)',  bg: 'var(--blue-bg)'         },
  pending_customer: { label: 'Awaiting You', color: 'var(--amber)',   bg: 'var(--amber-bg)'        },
  pending_ca:       { label: 'Awaiting CA',  color: 'var(--purple)',  bg: 'var(--purple-bg)'       },
  audit_completed:  { label: 'Completed',    color: 'var(--green)',   bg: 'var(--green-bg)'        },
  audit_abandoned:  { label: 'Abandoned',    color: 'var(--text2)',   bg: 'rgba(139,149,168,0.1)'  },
};

function StatusBadge({ status }) {
  const c = STATUS_CFG[status] || { label: 'No Status', color: 'var(--text2)', bg: 'rgba(139,149,168,0.1)' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 20,
      fontSize: 11, fontWeight: 600, background: c.bg, color: c.color,
      border: `1px solid ${c.color}33`, whiteSpace: 'nowrap',
    }}>{c.label}</span>
  );
}

// =============================================================================
// PAN Setup Banner — optional and dismissible; triggers self-claim after save
// =============================================================================
function PanSetupBanner({ onPanSaved, onDismiss, onCompaniesRefresh }) {
  const [pan,         setPan]         = useState('');
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState('');
  // Self-claim state
  const [claimResult, setClaimResult] = useState(null); // null | { result, ... }
  const [otp,         setOtp]         = useState('');
  const [customerId,  setCustomerId]  = useState('');
  const [otpSaving,   setOtpSaving]   = useState(false);
  const [otpError,    setOtpError]    = useState('');

  const handleSave = async () => {
    const p = pan.trim().toUpperCase();
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(p))
      return setError('Invalid PAN — expected: AAAAA9999A');
    setSaving(true); setError('');
    try {
      const res = await fetch(`${BASE}/users/me/pan`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({ pan_number: p }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save PAN');
      onPanSaved(data.pan_number);

      // Now try self-claim
      const claimRes = await fetch(`${BASE}/customers/self-claim/init`, {
        method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: '{}',
      });
      const claimData = await claimRes.json();
      if (claimRes.ok) {
        setClaimResult(claimData);
        if (claimData.customerId) setCustomerId(claimData.customerId);
        if (claimData.result === 'auto_linked' && onCompaniesRefresh) onCompaniesRefresh();
      }
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const handleVerifyOtp = async () => {
    if (!otp.trim()) return setOtpError('Please enter the OTP');
    setOtpSaving(true); setOtpError('');
    try {
      const res = await fetch(`${BASE}/customers/self-claim/verify-otp`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId, otp: otp.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'OTP verification failed');
      setClaimResult({ result: 'auto_linked', businessName: data.businessName });
      if (onCompaniesRefresh) onCompaniesRefresh();
    } catch (e) { setOtpError(e.message); }
    finally { setOtpSaving(false); }
  };

  // After claim result — show outcome instead of the normal form
  if (claimResult) {
    const { result, businessName, caName, maskedEmail } = claimResult;
    return (
      <div style={{
        background: 'var(--bg2)', border: '1px solid var(--border)',
        borderRadius: 12, padding: '16px 20px', marginBottom: 24, position: 'relative',
      }}>
        <button onClick={onDismiss} title="Dismiss" style={{
          position: 'absolute', top: 10, right: 12, background: 'none',
          border: 'none', cursor: 'pointer', color: 'var(--text3)', fontSize: 18, padding: 4,
        }}>×</button>

        {result === 'auto_linked' && (
          <>
            <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--green)', marginBottom: 4 }}>
              ✓ Account linked — {businessName}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text3)' }}>
              Your audit records with {caName || 'your CA'} are now linked. Refresh to see them below.
            </div>
          </>
        )}

        {result === 'otp_sent' && (
          <>
            <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)', marginBottom: 4 }}>
              Verify your contact email — {businessName}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 12 }}>
              A 6-digit code was sent to <strong>{maskedEmail}</strong> (the email your CA has on file).
              Enter it below to complete the link.
            </div>
            {otpError && (
              <div style={{ fontSize: 12, color: 'var(--red)', marginBottom: 10, background: 'var(--red-bg)', padding: '6px 10px', borderRadius: 6 }}>
                {otpError}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="text" maxLength={6} value={otp}
                onChange={e => { setOtp(e.target.value.replace(/\D/g, '')); setOtpError(''); }}
                placeholder="123456"
                onKeyDown={e => e.key === 'Enter' && handleVerifyOtp()}
                style={{
                  padding: '7px 12px', border: '1px solid var(--border)', borderRadius: 6,
                  background: 'var(--bg3)', color: 'var(--text)', fontSize: 20,
                  fontFamily: 'var(--mono)', letterSpacing: '0.3em', width: 140, outline: 'none',
                }}
              />
              <button onClick={handleVerifyOtp} disabled={otpSaving} style={{
                padding: '7px 16px', background: 'var(--accent)', border: 'none',
                borderRadius: 6, color: '#fff', fontSize: 12, fontWeight: 600,
                cursor: otpSaving ? 'not-allowed' : 'pointer', opacity: otpSaving ? 0.7 : 1,
                fontFamily: 'var(--sans)',
              }}>
                {otpSaving ? 'Verifying…' : 'Verify Code'}
              </button>
            </div>
          </>
        )}

        {result === 'not_found' && (
          <div style={{ fontSize: 13, color: 'var(--text3)' }}>
            PAN saved. No managed audit record found for your PAN yet — ask your CA to add you, or wait for an invite link.
          </div>
        )}

        {result === 'no_contact_email' && (
          <div style={{ fontSize: 13, color: 'var(--text3)' }}>
            PAN saved. Please ask your CA to add your email to their records or send you an invitation link.
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{
      background: 'var(--bg2)', border: '1px solid var(--border)',
      borderRadius: 12, padding: '16px 20px', marginBottom: 24,
      position: 'relative',
    }}>
      <button
        onClick={onDismiss}
        title="Dismiss"
        style={{
          position: 'absolute', top: 10, right: 12,
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--text3)', fontSize: 18, lineHeight: 1, padding: 4,
        }}
      >×</button>
      <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)', marginBottom: 4 }}>
        Have a CA who manages your books?
      </div>
      <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 12, maxWidth: 540 }}>
        Link your PAN to see audits your CA has done for you. Optional — you can sync your own Tally data without it.
      </div>
      {error && (
        <div style={{
          fontSize: 12, color: 'var(--red)', marginBottom: 10,
          background: 'var(--red-bg)', padding: '6px 10px', borderRadius: 6
        }}>
          {error}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="text" maxLength={10} value={pan}
          onChange={e => { setPan(e.target.value.toUpperCase()); setError(''); }}
          placeholder="AAAAA9999A"
          onKeyDown={e => e.key === 'Enter' && handleSave()}
          style={{
            padding: '7px 12px', border: '1px solid var(--border)', borderRadius: 6,
            background: 'var(--bg3)', color: 'var(--text)', fontSize: 13,
            fontFamily: 'var(--mono)', letterSpacing: '0.1em', width: 150, outline: 'none',
          }}
        />
        <button onClick={handleSave} disabled={saving} style={{
          padding: '7px 16px', background: 'var(--accent)', border: 'none',
          borderRadius: 6, color: '#fff', fontSize: 12, fontWeight: 600,
          cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1,
          fontFamily: 'var(--sans)',
        }}>
          {saving ? 'Saving…' : 'Link PAN'}
        </button>
        <button onClick={onDismiss} style={{
          padding: '7px 12px', background: 'transparent', border: 'none',
          color: 'var(--text3)', fontSize: 12,
          cursor: 'pointer', fontFamily: 'var(--sans)',
        }}>
          Skip for now
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// Sync List — loads all completed syncs for one company name
// (across all matching sources, regardless of book period)
// Clicking a row calls onOpenSync → navigates to full audit view
// =============================================================================
function SyncList({ companyName, visiblePages, onOpenSync }) {
  const [syncs, setSyncs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    fetch(
      `${BASE}/customers/my-companies/syncs?company=${encodeURIComponent(companyName)}`,
      { headers: getAuthHeaders() }
    )
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (!ok) throw new Error(d.error || 'Failed to load syncs');
        setSyncs(Array.isArray(d) ? d : []);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [companyName]);

  if (loading) return <div style={{ padding: '16px 0' }}><Skeleton rows={3} /></div>;

  if (error) return (
    <div style={{ padding: '12px 0', fontSize: 13, color: 'var(--red)' }}>{error}</div>
  );

  if (syncs.length === 0) return (
    <div style={{ padding: '16px 0', fontSize: 13, color: 'var(--text3)' }}>
      No completed syncs yet for this company.
    </div>
  );

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{
        fontSize: 11, fontWeight: 700, color: 'var(--text3)',
        textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10,
      }}>
        Sync History — {syncs.length} {syncs.length === 1 ? 'sync' : 'syncs'}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {syncs.map(run => (
          <div
            key={run.sync_id}
            onClick={() => onOpenSync({
              source_id: run.source_id,
              sync_id: run.sync_id,
              visible_pages: visiblePages ?? null,
            })}
            style={{
              padding: '12px 16px', borderRadius: 10, cursor: 'pointer',
              border: '1px solid var(--border)', background: 'var(--bg3)',
              display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = 'var(--accent)';
              e.currentTarget.style.background = 'var(--blue-bg)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = 'var(--border)';
              e.currentTarget.style.background = 'var(--bg3)';
            }}
          >
            {/* Date + period */}
            <div style={{ flex: 1, minWidth: 140 }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>
                {fmtDateTime(run.synced_at)}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                Period: {run.period_from || '—'} – {run.period_to || '—'}
              </div>
            </div>

            {/* Counts */}
            <div style={{ display: 'flex', gap: 14, fontSize: 12, flexShrink: 0 }}>
              <span style={{ color: 'var(--text2)' }}>
                {run.voucher_count ?? 0} vouchers
              </span>
              <span style={{ color: run.issue_count > 0 ? '#C0392B' : 'var(--text3)' }}>
                {run.issue_count ?? 0} issues
              </span>
            </div>

            {/* Audit status */}
            <StatusBadge status={run.audit_status} />

            {/* CTA arrow */}
            <span style={{
              fontSize: 12, fontWeight: 600, color: 'var(--accent)',
              flexShrink: 0, marginLeft: 'auto',
            }}>
              View Audit →
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// Company Card — expands to show SyncList
// =============================================================================
function CompanyCard({ company, onOpenSync }) {
  const [expanded, setExpanded] = useState(false);

  const lc = ({
    active:    { color: 'var(--green)',  bg: 'var(--green-bg)'        },
    suspended: { color: 'var(--amber)',  bg: 'var(--amber-bg)'        },
    cancelled: { color: 'var(--text2)', bg: 'rgba(139,149,168,0.1)'  },
  })[company.license_status] || { color: 'var(--text2)', bg: 'rgba(139,149,168,0.1)' };

  return (
    <div style={{
      background: 'var(--bg2)', border: '1px solid var(--border)',
      borderRadius: 12, overflow: 'hidden',
      boxShadow: expanded ? '0 2px 12px rgba(0,0,0,0.06)' : 'none',
      transition: 'box-shadow 0.2s',
    }}>
      {/* Header row */}
      <div
        onClick={() => setExpanded(e => !e)}
        style={{
          padding: '16px 20px', display: 'flex', alignItems: 'center',
          gap: 16, flexWrap: 'wrap', cursor: 'pointer', userSelect: 'none',
        }}
      >
        {/* Avatar */}
        <div style={{
          width: 44, height: 44, borderRadius: 10, flexShrink: 0,
          background: 'var(--blue-bg)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', fontSize: 18, fontWeight: 700, color: 'var(--accent)',
        }}>
          {company.company_name?.[0]?.toUpperCase() || '🏢'}
        </div>

        {/* Name + identifiers */}
        <div style={{ flex: 1, minWidth: 160 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)', marginBottom: 3 }}>
            {company.company_name}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text3)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {company.pan_number && (
              <span style={{ fontFamily: 'var(--mono)' }}>PAN: {company.pan_number}</span>
            )}
            {company.gstin && <span style={{ fontFamily: 'var(--mono)' }}>GSTIN: {company.gstin}</span>}
          </div>
        </div>

        {/* Last sync */}
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          {company.last_synced_at ? (
            <>
              <div style={{ fontSize: 12, color: 'var(--text2)', fontWeight: 500 }}>
                Last synced {fmtDate(company.last_synced_at)}
              </div>
              {company.period_from && (
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                  Period: {company.period_from} – {company.period_to}
                </div>
              )}
            </>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--text3)' }}>Not synced yet</div>
          )}
        </div>

        {/* License badge */}
        {company.license_status && (
          <span style={{
            padding: '3px 12px', borderRadius: 20, fontSize: 11, fontWeight: 600,
            background: lc.bg, color: lc.color, border: `1px solid ${lc.color}33`,
            flexShrink: 0, textTransform: 'capitalize',
          }}>
            {company.license_status}
          </span>
        )}

        {/* Access type tag */}
        {company.access_type === 'ca_managed' && company.ca_name && (
          <div style={{
            fontSize: 11, color: 'var(--text3)', flexShrink: 0,
            background: 'var(--bg3)', padding: '4px 10px', borderRadius: 6,
            border: '1px solid var(--border)',
          }}>
            CA: {company.ca_name}
          </div>
        )}
        {company.access_type === 'self_sync' && (
          <div style={{
            fontSize: 11, color: 'var(--text3)', flexShrink: 0,
            background: 'var(--bg3)', padding: '4px 10px', borderRadius: 6,
            border: '1px solid var(--border)',
          }}>
            Self-managed
          </div>
        )}

        {/* Chevron */}
        <span style={{
          color: 'var(--text3)', fontSize: 14, flexShrink: 0,
          transition: 'transform 0.2s', display: 'inline-block',
          transform: expanded ? 'rotate(180deg)' : 'none',
        }}>▾</span>
      </div>

      {/* Expanded: sync list */}
      {expanded && (
        <div style={{ padding: '0 20px 20px', borderTop: '1px solid var(--border)' }}>
          <SyncList
            companyName={company.company_name}
            visiblePages={company.visible_pages ?? null}
            onOpenSync={onOpenSync}
          />
        </div>
      )}
    </div>
  );
}

// =============================================================================
// My Engagements — CA-initiated audit engagements for this customer
// =============================================================================
const ENG_STATUS_CFG = {
  synced:           { label: 'Synced',          color: 'var(--green)',  bg: 'var(--green-bg)'        },
  in_progress:      { label: 'Under Review',    color: 'var(--accent)', bg: 'var(--blue-bg)'         },
  pending_customer: { label: 'Action Required', color: 'var(--amber)',  bg: 'var(--amber-bg)'        },
  pending_ca:       { label: 'Awaiting CA',     color: 'var(--purple)', bg: 'var(--purple-bg)'       },
  audit_completed:  { label: 'Completed',       color: 'var(--green)',  bg: 'var(--green-bg)'        },
  audit_abandoned:  { label: 'Abandoned',       color: 'var(--text2)',  bg: 'rgba(139,149,168,0.1)'  },
};

function EngStatusBadge({ status }) {
  const c = ENG_STATUS_CFG[status] || { label: status, color: 'var(--text2)', bg: 'rgba(139,149,168,0.1)' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 20,
      fontSize: 11, fontWeight: 600, background: c.bg, color: c.color,
      border: `1px solid ${c.color}33`, whiteSpace: 'nowrap',
    }}>{c.label}</span>
  );
}

// ── Star rating widget ────────────────────────────────────────────────────────
function StarRating({ value, onChange, readonly }) {
  const [hovered, setHovered] = useState(0);
  return (
    <span style={{ display: 'inline-flex', gap: 2, cursor: readonly ? 'default' : 'pointer' }}>
      {[1,2,3,4,5].map(i => (
        <span
          key={i}
          style={{ fontSize: 20, color: (hovered || value) >= i ? '#F59E0B' : 'var(--border)', lineHeight: 1 }}
          onMouseEnter={() => !readonly && setHovered(i)}
          onMouseLeave={() => !readonly && setHovered(0)}
          onClick={() => !readonly && onChange(i)}
        >★</span>
      ))}
    </span>
  );
}

// ── Review modal ──────────────────────────────────────────────────────────────
function ReviewModal({ engagement, existingReview, onClose, onSaved }) {
  const [rating,  setRating]  = useState(existingReview?.rating || 0);
  const [text,    setText]    = useState(existingReview?.review_text || '');
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');

  const submit = async () => {
    if (!rating) return setError('Please select a star rating');
    setSaving(true); setError('');
    try {
      const result = await api.submitCAReview({
        ca_user_id:    engagement.ca_user_id,
        engagement_id: engagement.id,
        rating,
        review_text:   text.trim() || null,
      });
      onSaved(result);
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: 'var(--bg)', borderRadius: 14, width: '100%', maxWidth: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 22px 14px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>
            {existingReview ? 'Edit Your Review' : 'Rate Your CA'}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--text3)', padding: '0 4px' }}>×</button>
        </div>
        <div style={{ padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontSize: 13, color: 'var(--text3)' }}>
            Rating for <strong>{engagement.ca_name || engagement.ca_email}</strong>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Your Rating *</label>
            <StarRating value={rating} onChange={setRating} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Review (optional)</label>
            <textarea
              rows={3}
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder="Share your experience…"
              style={{ padding: '9px 12px', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, resize: 'vertical', fontFamily: 'inherit' }}
            />
          </div>
          {error && <div style={{ fontSize: 13, color: '#dc2626', background: '#fef2f2', borderRadius: 8, padding: '8px 12px' }}>{error}</div>}
        </div>
        <div style={{ padding: '12px 22px 18px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} disabled={saving} style={{ padding: '8px 16px', background: 'var(--bg2)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
          <button onClick={submit} disabled={saving || !rating} style={{ padding: '8px 20px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer', opacity: (saving || !rating) ? 0.6 : 1 }}>
            {saving ? 'Saving…' : existingReview ? 'Update Review' : 'Submit Review'}
          </button>
        </div>
      </div>
    </div>
  );
}

function MyEngagements({ onOpenEngagement, inTab }) {
  const [engagements, setEngagements] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [reviewModal,  setReviewModal]  = useState(null);
  const [myReviews,    setMyReviews]    = useState({});
  const [pageSize,     setPageSize]     = useState(10);
  const [currentPage,  setCurrentPage]  = useState(1);

  useEffect(() => {
    setLoading(true); setError('');
    api.listMyEngagements()
      .then(rows => {
        const engs = Array.isArray(rows) ? rows : [];
        setEngagements(engs);
        setCurrentPage(1);
        const uniqueCAs = [...new Set(engs.map(e => e.ca_user_id).filter(Boolean))];
        uniqueCAs.forEach(caId => {
          api.getMyCAReview(caId)
            .then(r => { if (r) setMyReviews(prev => ({ ...prev, [caId]: r })); })
            .catch(() => {});
        });
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ marginBottom: 32 }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 14 }}>
        My Audit Engagements
      </div>
      <div style={{ background: 'var(--bg2)', borderRadius: 12, padding: 20 }}>
        <Skeleton rows={3} />
      </div>
    </div>
  );

  if (error) return (
    <div style={{ marginBottom: 32 }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 14 }}>
        My Audit Engagements
      </div>
      <div style={{ padding: '10px 14px', borderRadius: 8, background: 'var(--red-bg)', color: 'var(--red)', fontSize: 13 }}>
        {error}
      </div>
    </div>
  );

  if (engagements.length === 0) {
    if (!inTab) return null;
    return (
      <div className="cd-tab-empty">
        <div className="cd-tab-empty-icon">🤝</div>
        <div className="cd-tab-empty-title">No audit engagements yet</div>
        <div className="cd-tab-empty-desc">
          Audit engagements assigned to you by your CA will appear here. You can also request one using the button above.
        </div>
      </div>
    );
  }

  const totalPages     = Math.max(1, Math.ceil(engagements.length / pageSize));
  const startIdx       = (currentPage - 1) * pageSize;
  const endIdx         = Math.min(startIdx + pageSize, engagements.length);
  const visibleEngs    = engagements.slice(startIdx, endIdx);

  return (
    <div style={{ marginBottom: 32 }}>
      <div className="cd-mer-header">
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 10 }}>
          My Audit Engagements
          <span style={{
            fontSize: 12, fontWeight: 500, color: 'var(--text3)',
            background: 'var(--bg3)', padding: '2px 10px', borderRadius: 20,
            border: '1px solid var(--border)',
          }}>
            {engagements.length}
          </span>
        </div>
        <div className="cd-page-size">
          <span>Show</span>
          <select
            className="cd-page-size-select"
            value={pageSize}
            onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
          >
            {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <span>per page</span>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {visibleEngs.map(eng => {
          const isPending = eng.status === 'pending_customer';

          return (
            <div
              key={eng.id}
              onClick={() => onOpenEngagement(eng)}
              style={{
                background: 'var(--bg2)',
                border: isPending ? '1px solid #D9770633' : '1px solid var(--border)',
                borderRadius: 12, padding: '16px 20px', cursor: 'pointer',
                display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap',
                transition: 'all 0.15s',
                ...(isPending ? { boxShadow: '0 0 0 2px #D9770622' } : {}),
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = isPending ? '#D9770633' : 'var(--border)'; }}
            >
              {/* Avatar */}
              <div style={{
                width: 44, height: 44, borderRadius: 10, flexShrink: 0,
                background: 'var(--blue-bg)', display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontSize: 18, fontWeight: 700, color: 'var(--accent)',
              }}>
                {eng.company_name?.[0]?.toUpperCase() || '🏢'}
              </div>

              {/* Main info */}
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)', marginBottom: 3 }}>
                  {eng.company_name || 'Company'}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 6 }}>
                  CA: {eng.ca_name || eng.ca_email}
                  {eng.customer_pan && (
                    <span style={{ marginLeft: 12, fontFamily: 'var(--mono)' }}>
                      PAN: {eng.customer_pan}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                  {eng.engagement_id} · Created {fmtDate(eng.created_at)}
                  {eng.sync_count > 0 && (
                    <span style={{ marginLeft: 10 }}>{eng.sync_count} sync{eng.sync_count !== 1 ? 's' : ''}</span>
                  )}
                  {eng.latest_sync_at && (
                    <span style={{ marginLeft: 10 }}>· Last sync {fmtDate(eng.latest_sync_at)}</span>
                  )}
                </div>
              </div>

              {/* Status + CTA */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, flexShrink: 0 }}>
                <EngStatusBadge status={eng.status} />
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent)' }}>
                  View →
                </span>
              </div>

              {/* Review row — full width below */}
              {eng.ca_user_id && (
                <div
                  style={{ width: '100%', borderTop: '1px solid var(--border)', paddingTop: 10, marginTop: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}
                  onClick={e => e.stopPropagation()}
                >
                  {myReviews[eng.ca_user_id] ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ display: 'inline-flex', gap: 1 }}>
                        {[1,2,3,4,5].map(i => (
                          <span key={i} style={{ fontSize: 14, color: i <= myReviews[eng.ca_user_id].rating ? '#F59E0B' : 'var(--border)' }}>★</span>
                        ))}
                      </span>
                      <span style={{ fontSize: 12, color: 'var(--text3)' }}>Your rating</span>
                    </div>
                  ) : (
                    <span style={{ fontSize: 12, color: 'var(--text3)' }}>No review yet</span>
                  )}
                  <button
                    onClick={() => setReviewModal(eng)}
                    style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent)', background: 'none', border: '1px solid var(--accent)', borderRadius: 6, padding: '4px 12px', cursor: 'pointer' }}
                  >
                    {myReviews[eng.ca_user_id] ? 'Edit Review' : 'Rate CA'}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {totalPages > 1 && (
        <div className="cd-pagination">
          <button
            className="cd-page-btn"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            ← Prev
          </button>
          <span className="cd-page-info">Page {currentPage} of {totalPages}</span>
          <button
            className="cd-page-btn"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            Next →
          </button>
        </div>
      )}

      {reviewModal && (
        <ReviewModal
          engagement={reviewModal}
          existingReview={myReviews[reviewModal.ca_user_id] || null}
          onClose={() => setReviewModal(null)}
          onSaved={(r) => setMyReviews(prev => ({ ...prev, [reviewModal.ca_user_id]: r }))}
        />
      )}
    </div>
  );
}

// =============================================================================
// My Companies section
// =============================================================================
function MyCompanies({ pan, refreshKey, onOpenSync }) {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true); setError('');
    fetch(`${BASE}/customers/my-companies`, { headers: getAuthHeaders() })
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (!ok) throw new Error(d.error || 'Failed to load companies');
        setCompanies(Array.isArray(d) ? d : []);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [pan, refreshKey]);

  return (
    <div>
      <div style={{
        fontSize: 16, fontWeight: 700, color: 'var(--text)',
        marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10,
      }}>
        🏢 My Companies
        {!loading && (
          <span style={{
            fontSize: 12, fontWeight: 500, color: 'var(--text3)',
            background: 'var(--bg3)', padding: '2px 10px', borderRadius: 20,
            border: '1px solid var(--border)',
          }}>
            {companies.length} {companies.length === 1 ? 'company' : 'companies'}
          </span>
        )}
      </div>

      {error && (
        <div style={{
          padding: '10px 14px', borderRadius: 8, background: 'var(--red-bg)',
          color: 'var(--red)', fontSize: 13, marginBottom: 12
        }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ background: 'var(--bg2)', borderRadius: 12, padding: 20 }}>
          <Skeleton rows={3} />
        </div>
      ) : companies.length === 0 ? (
        <div style={{
          padding: '32px 24px', textAlign: 'center', background: 'var(--bg2)',
          borderRadius: 12, border: '1px solid var(--border)'
        }}>
          <div style={{ fontSize: 28, marginBottom: 10 }}>🔍</div>
          <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)', marginBottom: 6 }}>
            No companies yet
          </div>
          <div style={{ fontSize: 13, color: 'var(--text3)', maxWidth: 400, margin: '0 auto' }}>
            You haven't synced any Tally data yet. Install the AuditPrOS Sync agent
            on the computer with your Tally to get started, or ask your CA to link
            your account if they manage your audit.
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {companies.map(c => (
            <CompanyCard key={c.company_name} company={c} onOpenSync={onOpenSync} />
          ))}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Main CustomerDashboard
// =============================================================================
// =============================================================================
// Request Engagement Modal — customer-initiated engagement request
// =============================================================================
const SERVICE_TYPES = [
  { value: 'general_audit',   label: 'General Audit' },
  { value: 'tax_audit',       label: 'Tax Audit (u/s 44AB)' },
  { value: 'gst_filing',      label: 'GST Filing & Compliance' },
  { value: 'statutory_audit', label: 'Statutory Audit' },
  { value: 'due_diligence',   label: 'Due Diligence' },
  { value: 'other',           label: 'Other' },
];

const REQ_STATUS_CFG = {
  pending_admin:      { label: 'Under Review',      color: 'var(--amber)',  bg: 'var(--amber-bg)'        },
  ca_assigned:        { label: 'CA Assigned',       color: 'var(--accent)', bg: 'var(--blue-bg)'         },
  engagement_created: { label: 'Engagement Active', color: 'var(--green)',  bg: 'var(--green-bg)'        },
  rejected:           { label: 'Not Accepted',      color: 'var(--text2)',  bg: 'rgba(139,149,168,0.1)'  },
};

const ENG_STATUS_LABELS = {
  pending_customer: 'Awaiting Your Sync',
  in_progress:      'In Progress',
  synced:           'Synced',
  pending_ca:       'Awaiting CA',
  audit_completed:  'Audit Completed',
  audit_abandoned:  'Abandoned',
};

function RequestStatusBadge({ status }) {
  const c = REQ_STATUS_CFG[status] || REQ_STATUS_CFG.pending_admin;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 20,
      fontSize: 11, fontWeight: 600, background: c.bg, color: c.color,
      border: `1px solid ${c.color}33`, whiteSpace: 'nowrap',
    }}>{c.label}</span>
  );
}

function RequestEngagementModal({ onClose, onSubmitted }) {
  const [form, setForm] = useState({
    business_name: '', pan_number: '', gstin: '',
    service_type: 'general_audit', description: '',
    sync_responsibility: 'customer', data_sharing_consent: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    setError('');
    if (!form.business_name.trim()) return setError('Business name is required');
    if (form.sync_responsibility === 'ca' && !form.data_sharing_consent) {
      return setError('Please accept the data sharing terms to authorise your CA to sync on your behalf');
    }
    setSaving(true);
    try {
      const result = await api.createEngagementRequest(form);
      setSuccess(result.request_id);
      onSubmitted?.(result);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: 'var(--bg)', borderRadius: 14, width: '100%', maxWidth: 480,
        maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 22px 14px', borderBottom: '1px solid var(--border)',
        }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>
            Request an Audit Engagement
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 20, color: 'var(--text3)', lineHeight: 1, padding: '0 4px',
          }}>×</button>
        </div>

        {success ? (
          <div style={{ padding: '28px 22px', textAlign: 'center' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>✓</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--green)', marginBottom: 8 }}>
              Request Submitted
            </div>
            <div style={{ fontFamily: 'monospace', fontSize: 18, fontWeight: 700, color: 'var(--accent)', marginBottom: 12 }}>
              {success}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 24, lineHeight: 1.6 }}>
              Your request has been received. Our team will review it and assign a
              qualified CA to your engagement. You will receive an email confirmation.
            </div>
            <button
              onClick={onClose}
              style={{
                padding: '10px 24px', background: 'var(--accent)', color: '#fff',
                border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer',
              }}
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <div style={{ padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[
                { key: 'business_name', label: 'Business / Company Name', placeholder: 'e.g. Acme Pvt. Ltd.', required: true },
              ].map(({ key, label, placeholder, required }) => (
                <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {label} {required && <span style={{ color: '#dc2626' }}>*</span>}
                  </label>
                  <input
                    type="text"
                    placeholder={placeholder}
                    value={form[key]}
                    onChange={e => set(key, e.target.value)}
                    style={{
                      padding: '9px 12px', border: '1px solid var(--border)', borderRadius: 8,
                      background: 'var(--bg2)', color: 'var(--text)', fontSize: 13,
                    }}
                  />
                </div>
              ))}

              <div style={{ display: 'flex', gap: 12 }}>
                {[
                  { key: 'pan_number', label: 'PAN', placeholder: 'AAAAA9999A' },
                  { key: 'gstin', label: 'GSTIN (optional)', placeholder: '27AAAAA9999A1Z5' },
                ].map(({ key, label, placeholder }) => (
                  <div key={key} style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      {label}
                    </label>
                    <input
                      type="text"
                      placeholder={placeholder}
                      value={form[key]}
                      onChange={e => set(key, e.target.value.trim().toUpperCase())}
                      style={{
                        padding: '9px 12px', border: '1px solid var(--border)', borderRadius: 8,
                        background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, fontFamily: 'monospace',
                      }}
                    />
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Type of Service
                </label>
                <select
                  value={form.service_type}
                  onChange={e => set('service_type', e.target.value)}
                  style={{
                    padding: '9px 12px', border: '1px solid var(--border)', borderRadius: 8,
                    background: 'var(--bg2)', color: 'var(--text)', fontSize: 13,
                  }}
                >
                  {SERVICE_TYPES.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Additional Details (optional)
                </label>
                <textarea
                  rows={3}
                  placeholder="Describe any specific requirements, fiscal year, or other context…"
                  value={form.description}
                  onChange={e => set('description', e.target.value)}
                  style={{
                    padding: '9px 12px', border: '1px solid var(--border)', borderRadius: 8,
                    background: 'var(--bg2)', color: 'var(--text)', fontSize: 13,
                    resize: 'vertical', fontFamily: 'inherit',
                  }}
                />
              </div>

              {/* Sync responsibility */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Who will sync the Tally data?
                </label>
                {[
                  { value: 'customer', label: 'I will sync it myself', desc: 'You connect your Tally and push data to AuditPro.' },
                  { value: 'ca',       label: 'My CA will sync on my behalf', desc: 'Your CA will access and sync your Tally data for you.' },
                ].map(opt => (
                  <label
                    key={opt.value}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
                      padding: '10px 12px', borderRadius: 8, border: `1px solid ${form.sync_responsibility === opt.value ? 'var(--accent)' : 'var(--border)'}`,
                      background: form.sync_responsibility === opt.value ? 'rgba(79,142,247,0.06)' : 'var(--bg2)',
                    }}
                  >
                    <input
                      type="radio"
                      name="sync_responsibility"
                      value={opt.value}
                      checked={form.sync_responsibility === opt.value}
                      onChange={() => set('sync_responsibility', opt.value)}
                      style={{ marginTop: 2, flexShrink: 0 }}
                    />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{opt.label}</div>
                      <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>{opt.desc}</div>
                    </div>
                  </label>
                ))}
              </div>

              {/* Consent — only shown when CA syncs */}
              {form.sync_responsibility === 'ca' && (
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', padding: '10px 12px', borderRadius: 8, background: 'rgba(245,166,35,0.1)', border: '1px solid rgba(245,166,35,0.3)' }}>
                  <input
                    type="checkbox"
                    checked={form.data_sharing_consent}
                    onChange={e => set('data_sharing_consent', e.target.checked)}
                    style={{ marginTop: 2, flexShrink: 0 }}
                  />
                  <div style={{ fontSize: 12, color: '#92400E', lineHeight: 1.6 }}>
                    I authorise the assigned Chartered Accountant to access and sync my financial data
                    from Tally on my behalf for the purpose of this audit engagement. I understand that
                    this consent can be withdrawn by contacting support.
                  </div>
                </label>
              )}

              {error && (
                <div style={{ fontSize: 13, color: '#dc2626', background: '#fef2f2', borderRadius: 8, padding: '8px 12px', border: '1px solid #fca5a5' }}>
                  {error}
                </div>
              )}

              <div style={{ fontSize: 12, color: 'var(--text3)', padding: '8px 12px', background: 'var(--bg2)', borderRadius: 8, border: '1px solid var(--border)', lineHeight: 1.6 }}>
                Your request will be reviewed by our team. We will assign a qualified
                Chartered Accountant and notify you by email once the engagement is set up.
              </div>
            </div>

            <div style={{
              padding: '14px 22px 18px', borderTop: '1px solid var(--border)',
              display: 'flex', justifyContent: 'flex-end', gap: 10,
            }}>
              <button
                onClick={onClose}
                disabled={saving}
                style={{
                  padding: '8px 16px', background: 'var(--bg2)', color: 'var(--text)',
                  border: '1px solid var(--border)', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={saving}
                style={{
                  padding: '8px 20px', background: 'var(--accent)', color: '#fff',
                  border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer',
                  opacity: saving ? 0.6 : 1,
                }}
              >
                {saving ? 'Submitting…' : 'Submit Request'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// My Engagement Requests — customer's self-submitted requests to admin
// =============================================================================
function MyEngagementRequests({ refreshKey, onOpenEngagement, inTab }) {
  const [requests,    setRequests]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');
  const [pageSize,    setPageSize]    = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setLoading(true); setError(''); setCurrentPage(1);
    api.listEngagementRequests()
      .then(rows => setRequests(Array.isArray(rows) ? rows : []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [refreshKey]);

  if (loading) return (
    <div style={{ background: 'var(--bg2)', borderRadius: 12, padding: 20 }}>
      <Skeleton rows={3} />
    </div>
  );

  if (error) return (
    <div style={{ padding: '10px 14px', borderRadius: 8, background: 'var(--red-bg)', color: 'var(--red)', fontSize: 13 }}>
      {error}
    </div>
  );

  if (requests.length === 0) {
    if (!inTab) return null;
    return (
      <div className="cd-tab-empty">
        <div className="cd-tab-empty-icon">📋</div>
        <div className="cd-tab-empty-title">No engagement requests yet</div>
        <div className="cd-tab-empty-desc">
          Click <strong>+ Request Engagement</strong> above to submit a request. Our team will review it and assign a CA.
        </div>
      </div>
    );
  }

  const totalPages  = Math.max(1, Math.ceil(requests.length / pageSize));
  const startIdx    = (currentPage - 1) * pageSize;
  const endIdx      = Math.min(startIdx + pageSize, requests.length);
  const visibleReqs = requests.slice(startIdx, endIdx);

  return (
    <div style={{ marginBottom: 32 }}>
      <div className="cd-mer-header">
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 10 }}>
          My Engagement Requests
          <span style={{
            fontSize: 12, fontWeight: 500, color: 'var(--text3)',
            background: 'var(--bg3)', padding: '2px 10px', borderRadius: 20,
            border: '1px solid var(--border)',
          }}>
            {requests.length}
          </span>
        </div>
        <div className="cd-page-size">
          <span>Show</span>
          <select
            className="cd-page-size-select"
            value={pageSize}
            onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
          >
            {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <span>per page</span>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {visibleReqs.map(r => (
          <div key={r.id} style={{
            background: 'var(--bg2)', border: '1px solid var(--border)',
            borderRadius: 12, padding: '14px 18px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 12, flexWrap: 'wrap',
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)', marginBottom: 2 }}>
                {r.business_name}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ fontFamily: 'monospace', color: 'var(--accent)' }}>{r.request_id}</span>
                {r.pan_number && <span>PAN: {r.pan_number}</span>}
                <span>{new Date(r.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
              </div>
              {r.status === 'engagement_created' && r.ca_name && (
                <div style={{ fontSize: 12, color: 'var(--green)', marginTop: 4 }}>
                  CA: {r.ca_name}
                  {r.engagement_number && <> · <span style={{ fontFamily: 'monospace' }}>EN-{String(r.engagement_number).padStart(8,'0')}</span></>}
                  {r.engagement_status && (
                    <> · {ENG_STATUS_LABELS[r.engagement_status] || r.engagement_status}</>
                  )}
                </div>
              )}
              {r.sync_responsibility && r.status !== 'rejected' && (
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                  Sync: {r.sync_responsibility === 'ca' ? 'CA will sync on your behalf' : 'You sync'}
                  {r.data_sharing_consent && <> · Consent given</>}
                </div>
              )}
              {r.status === 'engagement_created' && r.engagement_id && r.sync_responsibility !== 'ca' && onOpenEngagement && (
                <button
                  onClick={e => { e.stopPropagation(); onOpenEngagement({ id: r.engagement_id }); }}
                  style={{
                    marginTop: 8, padding: '6px 14px', background: 'var(--accent)', color: '#fff',
                    border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 12, cursor: 'pointer',
                  }}
                >
                  View Engagement & Sync →
                </button>
              )}
              {r.status === 'rejected' && r.admin_note && (
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4, fontStyle: 'italic' }}>
                  {r.admin_note}
                </div>
              )}
            </div>
            <RequestStatusBadge status={r.status} />
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="cd-pagination">
          <button
            className="cd-page-btn"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            ← Prev
          </button>
          <span className="cd-page-info">Page {currentPage} of {totalPages}</span>
          <button
            className="cd-page-btn"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

const TABS = [
  { key: 'requests',    label: 'My Engagement Requests' },
  { key: 'engagements', label: 'My Audit Engagements'   },
  { key: 'companies',   label: 'My Companies'           },
];

export default function CustomerDashboard({ userRole, onOpenSync, onOpenEngagement }) {
  const isMobile = useIsMobile();
  const [pan, setPan] = useState(userRole?.pan_number || null);
  const [companiesKey, setCompaniesKey] = useState(0);
  const [claimBanner, setClaimBanner] = useState(null); // { type: 'success'|'error', msg }
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestsKey, setRequestsKey] = useState(0);
  const [activeTab, setActiveTab] = useState('requests');

  // PAN nudge is optional and dismissible — persisted in localStorage
  const [showPanPrompt, setShowPanPrompt] = useState(() => {
    return !userRole?.pan_number
      && localStorage.getItem('pan_prompt_dismissed') !== 'true';
  });

  const dismissPanPrompt = () => {
    setShowPanPrompt(false);
    localStorage.setItem('pan_prompt_dismissed', 'true');
  };

  // Process a magic-link claim token stored in sessionStorage after login
  useEffect(() => {
    const token = sessionStorage.getItem('auditpros_pending_claim');
    if (!token) return;
    sessionStorage.removeItem('auditpros_pending_claim');

    fetch(`${BASE}/customers/claim/${encodeURIComponent(token)}`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: '{}',
    })
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (ok) {
          setClaimBanner({ type: 'success', msg: `You've been linked to ${d.businessName}. Your CA's audit data is now visible below.` });
          setCompaniesKey(k => k + 1);
        } else {
          setClaimBanner({ type: 'error', msg: d.error || 'Failed to process invitation link.' });
        }
      })
      .catch(() => setClaimBanner({ type: 'error', msg: 'Failed to process invitation link.' }));
  }, []);

  return (
    <PageWrap>
      <div className="cd-page-header">
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
            My Audit Dashboard
          </div>
          <div style={{ fontSize: 13, color: 'var(--text3)' }}>
            Sync your Tally data and view audit reports — yours and any your CA shares with you.
          </div>
        </div>
        <button
          onClick={() => setShowRequestModal(true)}
          style={{
            padding: '9px 18px', background: 'var(--accent)', color: '#fff',
            border: 'none', borderRadius: 9, fontWeight: 600, fontSize: 13, cursor: 'pointer',
            whiteSpace: 'nowrap', flexShrink: 0,
          }}
        >
          + Request Engagement
        </button>
      </div>

      {claimBanner && (
        <div style={{
          padding: '12px 16px', borderRadius: 10, marginBottom: 16,
          background: claimBanner.type === 'success' ? 'var(--green-bg)' : 'var(--red-bg)',
          color: claimBanner.type === 'success' ? 'var(--green)' : 'var(--red)',
          border: `1px solid ${claimBanner.type === 'success' ? 'rgba(62,207,122,0.3)' : 'rgba(224,82,82,0.3)'}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13,
        }}>
          <span>{claimBanner.type === 'success' ? '✓ ' : ''}{claimBanner.msg}</span>
          <button onClick={() => setClaimBanner(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'inherit', padding: '0 4px' }}>×</button>
        </div>
      )}

      {showPanPrompt && (
        <PanSetupBanner
          onPanSaved={p => { setPan(p); setShowPanPrompt(false); }}
          onDismiss={dismissPanPrompt}
          onCompaniesRefresh={() => setCompaniesKey(k => k + 1)}
        />
      )}

      {/* Tab bar — dropdown on mobile, button tabs on desktop */}
      {isMobile ? (
        <div className="cd-tab-select-wrap">
          <select
            className="cd-tab-select"
            value={activeTab}
            onChange={e => setActiveTab(e.target.value)}
          >
            {TABS.map(t => (
              <option key={t.key} value={t.key}>{t.label}</option>
            ))}
          </select>
          <span className="cd-tab-select-arrow">▾</span>
        </div>
      ) : (
        <div className="cd-tabs">
          {TABS.map(t => (
            <button
              key={t.key}
              className={`cd-tab${activeTab === t.key ? ' cd-tab--active' : ''}`}
              onClick={() => setActiveTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {activeTab === 'requests' && (
        <MyEngagementRequests refreshKey={requestsKey} onOpenEngagement={onOpenEngagement} inTab />
      )}

      {activeTab === 'engagements' && (
        <MyEngagements onOpenEngagement={onOpenEngagement} inTab />
      )}

      {activeTab === 'companies' && (
        <>
          <AgentSyncBar />
          <MyCompanies pan={pan} refreshKey={companiesKey} onOpenSync={onOpenSync} />
        </>
      )}

      {showRequestModal && (
        <RequestEngagementModal
          onClose={() => setShowRequestModal(false)}
          onSubmitted={() => setRequestsKey(k => k + 1)}
        />
      )}
    </PageWrap>
  );
}